<!--
 * Locale Selector Component - Language & Currency monochrome icons for NavBar
 * 地区选择器组件 - NavBar 中使用单色 Material Design 图标
 *
 * Shows monochrome "translate" icon for language and "currency_exchange" icon for currency.
 * Each icon opens its own bottom-sheet popup.
 * 导航栏右侧显示单色 "translate" 图标（语言）和 "currency_exchange" 图标（货币）。
 * 每个图标打开各自独立的底部弹窗。
 -->
<style scoped lang="sass">
.locale_selector
  display: inline-flex
  align-items: center
  gap: 2px

  .icon_btn
    width: 30px
    height: 30px
    flex-shrink: 0
    display: flex
    align-items: center
    justify-content: center
    cursor: pointer
    transition: all 0.2s ease

    &:active
      transform: scale(0.9)
      opacity: 0.7
</style>

<template lang="pug">
.locale_selector(v-if="hasMultipleLanguages || hasMultipleCurrencies")
  //- Language icon / 语言图标
  .icon_btn(v-if="hasMultipleLanguages" @click="openLanguageModal")
    icon(size="22" :color="headerColour") translate

  //- Currency icon / 货币图标
  .icon_btn(v-if="hasMultipleCurrencies" @click="openCurrencyModal")
    icon(size="22" :color="headerColour") currency_exchange

  //- Language Modal / 语言弹窗
  language-selector-modal(
    :show="showLanguageModal"
    :languages="languages"
    :currentLanguage="currentLanguage"
    @select="selectLanguage"
    @close="closeLanguageModal"
  )

  //- Currency Modal / 货币弹窗
  currency-selector-modal(
    :show="showCurrencyModal"
    :currencies="currencies"
    :currentCurrency="currentCurrency"
    @select="selectCurrency"
    @close="closeCurrencyModal"
  )
</template>

<script>
import Vue from 'vue'
import Component from 'vue-class-component'
import { mapGetters, mapState } from 'vuex'
import LanguageSelectorModal from './LanguageSelectorModal.vue'
import CurrencySelectorModal from './CurrencySelectorModal.vue'

@Component({
  components: {
    LanguageSelectorModal,
    CurrencySelectorModal,
  },

  computed: {
    ...mapState('language', {
      currentLanguage: 'currentLanguage',
      supportedLanguages: 'supportedLanguages',
    }),
    ...mapGetters('language', ['hasMultipleLanguages']),
    ...mapState('currencySettings', {
      currentCurrency: 'currentCurrency',
      supportedCurrencies: 'supportedCurrencies',
    }),
    ...mapGetters('currencySettings', ['hasMultipleCurrencies']),
  },
})
class LocaleSelector extends Vue {
  showLanguageModal = false
  showCurrencyModal = false

  get headerColour() {
    return 'var(--menu-header-colour, #fff)'
  }

  get languages() {
    return this.supportedLanguages || []
  }

  get currencies() {
    return this.supportedCurrencies || []
  }

  openLanguageModal() {
    this.showLanguageModal = true
  }

  closeLanguageModal() {
    this.showLanguageModal = false
  }

  openCurrencyModal() {
    this.showCurrencyModal = true
  }

  closeCurrencyModal() {
    this.showCurrencyModal = false
  }

  selectLanguage(langCode) {
    const prevLang = this.currentLanguage
    this.$store.dispatch('language/changeLanguage', langCode)
    this.showLanguageModal = false

    if (langCode !== prevLang) {
      const langName = this.languages.find(l => l.code === langCode)?.name || langCode
      if (this.$openreplayTracker) {
        this.$openreplayTracker.event('switch_language', { from: prevLang, to: langCode })
      }
      if (this.$gtag) {
        this.$gtag.event('switch_language', {
          event_category: 'user_preference',
          event_label: langName,
          from_language: prevLang,
          to_language: langCode,
        })
      }
    }
  }

  selectCurrency(currCode) {
    const prevCurrency = this.currentCurrency
    this.$store.dispatch('currencySettings/changeCurrency', currCode)
    this.showCurrencyModal = false

    if (currCode !== prevCurrency) {
      const currName = this.currencies.find(c => c.code === currCode)?.name || currCode
      if (this.$openreplayTracker) {
        this.$openreplayTracker.event('switch_currency', { from: prevCurrency, to: currCode })
      }
      if (this.$gtag) {
        this.$gtag.event('switch_currency', {
          event_category: 'user_preference',
          event_label: currName,
          from_currency: prevCurrency,
          to_currency: currCode,
        })
      }
    }
  }
}

export default LocaleSelector
</script>
